import { Component, inject } from '@angular/core';
import { FormsModule, NgForm } from '@angular/forms';
import { Router, RouterModule } from '@angular/router';
import { ButtonModule } from 'primeng/button';
import { InputTextModule } from 'primeng/inputtext';
import { MessageService } from 'primeng/api';
import { CommonModule } from '@angular/common';
import { jwtDecode } from 'jwt-decode';
import { AuthService } from '../../services/account/auth.service';
import { DecodedToken } from '../../guards/permission.guard';
import { loginResponse } from '../../models/account/loginResponse';

@Component({
  selector: 'app-partner-sign-in',
  standalone: true,
  imports: [ButtonModule, FormsModule, RouterModule, InputTextModule, CommonModule],
  templateUrl: './partner-sign-in.component.html',
  styleUrl: './partner-sign-in.component.scss'
})
export class PartnerSignInComponent {
  private authService = inject(AuthService);
  private router = inject(Router);
  private messageService = inject(MessageService);

  loading = false;
  email = '';
  password = '';

  onSubmit(form: NgForm): void {
    if (form.invalid) {
      return;
    }

    this.loading = true;
    this.authService.login({
      emailOrPhone: this.email,
      password: this.password
    }).subscribe({
      next: (response: loginResponse) => {
        if (!response?.token) {
          this.loading = false;
          return;
        }

        const decodedToken = jwtDecode<DecodedToken>(response.token);
        const rolesClaim = decodedToken['http://schemas.microsoft.com/ws/2008/06/identity/claims/role'];
        const roles: string[] = Array.isArray(rolesClaim) ? rolesClaim : rolesClaim ? [rolesClaim] : [];
        const targetPath = this.resolveNonStudentLandingRoute(roles);

        if (!targetPath) {
          this.messageService.add({
            severity: 'error',
            summary: 'Unauthorized',
            detail: 'Student users should use Student Sign In.'
          });
          this.loading = false;
          return;
        }

        localStorage.setItem('account', JSON.stringify(response));
        this.router.navigate([targetPath]);
        this.loading = false;
      },
      error: (error: Error) => {
        this.messageService.add({
          severity: 'error',
          summary: error.name,
          detail: error.message
        });
        this.loading = false;
      },
      complete: () => {
        this.loading = false;
      }
    });
  }

  private resolveNonStudentLandingRoute(roles: string[]): string | null {
    if (roles.includes('SuperAdmin')) {
      return '/admin/colleges';
    }
    if (roles.includes('Admin')) {
      return '/admin/peer-management';
    }
    if (roles.includes('SchoolAdmin')) {
      return '/school-portal/students';
    }
    if (roles.includes('Mentor')) {
      return '/mentor-portal/schedule-meetings';
    }
    if (roles.includes('Operations')) {
      return '/operations-portal/dashboard';
    }
    if (roles.includes('PeerReviewer')) {
      return '/peer-portal/timings';
    }
    return null;
  }
}
